import { UserRole } from '@mairistem/finances-utils';
import { App, Feature, Menu } from '@mairistem/module';
import { request } from '@mairistem/tools';
import React from 'react';
import ArbitrageMasse from './components/arbitrageMasse/ArbitrageMasse';
import Context from './components/common/Context/Context';
import {
	chargerSimulationCourante,
	useSimulationCourante,
} from './components/common/Context/simulationStore';
import { useUserRoleCourant } from './components/common/Context/userRoleStore';
import Parametres from './components/parametres/Parametres';
import ElaborationBudget from './components/simulation/ElaborationBudget';
import StorageKey from './constants/common/storage';
import { isArbitrageMasseDisponible } from './utils/simulation/simulation';
import 'react-toastify/dist/ReactToastify.css';
import './AppSimulation.css';

const getIdentifiantSimulationFromUrl = () => {
	const { pathname } = window.location;
	// Diviser le chemin en segments
	const segments = pathname.split('/');
	// Récupérer le dernier segment
	const lastSegment = segments.pop() || segments.pop();
	// Pour gérer les barres obliques finales
	return lastSegment;
};

const renderBreadcrumb = (
	path,
	{ BreadcrumbContainer, BreadcrumbSection, BreadcrumbSeparator },
) => (
	<BreadcrumbContainer>
		<BreadcrumbSection key="domain">
			{path.domain.toLocaleUpperCase()}
		</BreadcrumbSection>
		<BreadcrumbSeparator />
		<BreadcrumbSection key="feature">{path.feature}</BreadcrumbSection>
	</BreadcrumbContainer>
);

const AppSimulation = () => {
	const identifiantSimulationRef = React.useRef(
		getIdentifiantSimulationFromUrl(),
	);
	const userRole = useUserRoleCourant();
	const arbitrageMasseDisponible = useSimulationCourante((simulation) =>
		isArbitrageMasseDisponible(simulation, userRole),
	);
	// Les contenus des features doivent garder la meme reference d'un rendu a
	// l'autre : @mairistem/module compare l'element et, s'il differe, remonte le
	// contenu dans une nouvelle racine React (et relance donc tous ses appels API)
	const contenuParametres = React.useMemo(() => <Parametres />, []);
	const contenuElaborationBudget = React.useMemo(
		() => (
			<ElaborationBudget
				identifiantSimulation={Number(identifiantSimulationRef.current)}
			/>
		),
		[],
	);
	const contenuArbitrageMasse = React.useMemo(
		() => (
			<ArbitrageMasse
				identifiantSimulation={Number(identifiantSimulationRef.current)}
			/>
		),
		[],
	);
	const [, forcerRendu] = React.useReducer(
		(compteur: number) => compteur + 1,
		0,
	);

	// Le role n'est connu qu'une fois le Context monte (apres authentification
	// et passage de version) ; la simulation n'est interrogee que si le role
	// permet l'arbitrage de masse
	React.useEffect(() => {
		if (userRole === undefined || userRole < UserRole.CHARGE_PILOTAGE) return;

		chargerSimulationCourante(Number(identifiantSimulationRef.current)).catch(
			() => {},
		);
	}, [userRole]);

	// Un Menu s'enregistre dans le manifest pendant son propre rendu, qui a lieu
	// apres celui du Shell : un menu ajoute apres coup n'apparait donc qu'au
	// rendu suivant du Shell, que l'on provoque ici
	React.useEffect(() => {
		if (arbitrageMasseDisponible) forcerRendu();
	}, [arbitrageMasseDisponible]);

	const handleOnAuth = async () => {
		// Gestion de l'identifiantExercice
		const queryParams = new URLSearchParams(window.location.search);
		sessionStorage.setItem(
			StorageKey.IDENTIFIANT_EXERCICE,
			queryParams.get('identifiantExercice'),
		);

		sessionStorage.setItem(
			StorageKey.IDENTIFIANT_ENTITE,
			queryParams.get(StorageKey.IDENTIFIANT_ENTITE),
		);
	};

	return (
		<App
			breadcrumbRenderer={renderBreadcrumb}
			code="HOL_SIMU"
			context={Context}
			group="finances"
			name="Elaboration du budget"
			request={request}
			onAuth={handleOnAuth}
			additionalProductCodesUpdate={['HOL_CPTA', '360_DEC']}
		>
			<Menu icon="cog" name="Paramètres" path="/parametres">
				<Feature>
					<Feature.Content>{contenuParametres}</Feature.Content>
				</Feature>
			</Menu>
			<Menu
				icon="home"
				name="Elaboration du budget"
				path={`/budgets/${identifiantSimulationRef.current}`}
			>
				<Feature>
					<Feature.Content>{contenuElaborationBudget}</Feature.Content>
				</Feature>
			</Menu>
			{arbitrageMasseDisponible && (
				<Menu
					icon="balance scale"
					name="Arbitrage de masse"
					path={`/arbitrages/${identifiantSimulationRef.current}`}
				>
					<Feature>
						<Feature.Content>{contenuArbitrageMasse}</Feature.Content>
					</Feature>
				</Menu>
			)}
		</App>
	);
};
export default AppSimulation;
